// src/lib/db/bots.ts
import { supabase } from '@/lib/supabaseClient';
import { validateUUID, validateUUIDs, logBotOperation } from '@/utils/validation';

/**
 * Fetch bot's quote currency from bot settings
 * Priority: bots.quote_currency > settings.quoteCurrency > settings.quote_currency > null
 */
export async function fetchBotQuoteCurrency(botId: string): Promise<string | null> {
  if (!botId) return null;

  // Validate bot ID
  if (!validateUUID(botId, 'Bot ID')) {
    return null;
  }

  // Try to fetch from bots table
  const { data, error } = await supabase
    .from('bots')
    .select('id, quote_currency, settings')
    .eq('id', botId)
    .maybeSingle();

  if (error) {
    console.warn('[BOT QUOTE] bots error', error);
    return null;
  }

  // 1) Direct field: bots.quote_currency
  const direct = data?.quote_currency;

  // 2) From settings JSON: settings.quoteCurrency or settings.quote_currency
  const fromSettings = data?.settings?.quoteCurrency || data?.settings?.quote_currency || null;

  const chosen = direct || fromSettings || null;
  return chosen;
}

/**
 * Clone a bot with all its settings (V2 - 100% server-driven naming)
 * Returns: new bot full row with all settings directly from RPC
 *
 * Server generates unique name automatically using f_next_bot_name.
 * No client-side name guessing needed!
 */
export async function cloneBotV2(sourceBotId: string, userId: string) {
  // Validate inputs
  if (!sourceBotId || !userId) {
    throw new Error('sourceBotId and userId are required');
  }

  // ✅ Validate UUIDs using centralized utility
  if (!validateUUIDs({ 'User ID': userId, 'Source Bot ID': sourceBotId })) {
    throw new Error('Invalid UUID format detected');
  }

  // ✅ Enhanced debug logging
  logBotOperation('CLONE BOT V2 - START', {
    userId,
    botId: sourceBotId,
    timestamp: new Date().toISOString()
  });

  // First, fetch source bot to see its name
  const { data: sourceBot, error: fetchError } = await supabase
    .from('bots')
    .select('id, name, user_id')
    .eq('id', sourceBotId)
    .eq('user_id', userId)
    .maybeSingle();

  if (fetchError) {
    console.error('[CLONE BOT V2] Error fetching source bot:', fetchError);
  } else if (sourceBot) {
    console.log('[CLONE BOT V2] Source bot details:', {
      id: sourceBot.id,
      name: sourceBot.name,
      userId: sourceBot.user_id
    });
  } else {
    console.warn('[CLONE BOT V2] Source bot not found or access denied');
  }

  console.log('[CLONE BOT V2] Calling RPC f_bots_clone_bot_v2 with:', {
    p_user_id: userId,
    p_source_bot_id: sourceBotId
  });

  const { data, error } = await supabase.rpc('f_bots_clone_bot_v2', {
    p_user_id: userId,
    p_source_bot_id: sourceBotId,
  });

  console.log('[CLONE BOT V2] RPC response:', {
    hasData: !!data,
    dataId: data?.dataId,
    dataName: data?.dataName,
    success: data?.success,
    error: error,
    errorMessage: error?.message,
    errorDetails: error?.details,
    errorHint: error?.hint,
    errorCode: error?.code
  });

  if (error) {
    console.error('[CLONE BOT V2] RPC error details:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
      fullError: error
    });
    throw new Error(error?.message ?? 'Clone RPC failed');
  }

  // Check for error in response data
  if (data?.error) {
    console.error('[CLONE BOT V2] Server error:', data.error, data.details);
    throw new Error(`Clone failed: ${data.error}`);
  }

  // Check for dataId and dataName (new format)
  if (!data || !data.dataId || !data.dataName) {
    console.error('[CLONE BOT V2] Invalid RPC response - missing dataId or dataName:', data);
    throw new Error('Clone failed: RPC returned incomplete data');
  }

  const clonedName = data.dataName;
  const clonedId = data.dataId;
  const parentId = data.parent_id;

  console.info('[CLONE BOT V2] ✅ Clone successful!', {
    sourceBot: sourceBot?.name,
    clonedBot: clonedName,
    clonedId: clonedId,
    parentId: parentId,
    hierarchy: parentId ? `${sourceBot?.name} → ${clonedName}` : `${clonedName} (root)`,
    namingStrategy: 'server-authoritative with WHILE loop'
  });

  // Return in format expected by caller
  return {
    id: clonedId,
    name: clonedName,
    dataId: clonedId,
    dataName: clonedName,
    parent_id: parentId,
    success: data.success,
    counter: data.counter,
    base_name: data.base_name
  };
}

/**
 * Clone a bot with all its settings (V1 - legacy)
 * Returns: new bot ID
 * @deprecated Use cloneBotV2 instead
 */
export async function cloneBot(sourceBotId: string, userId: string, newName?: string) {
  const { data, error } = await supabase.rpc('f_bots_clone_bot', {
    p_user_id: userId,
    p_source_bot_id: sourceBotId,
    p_new_name: newName ?? null,
  });

  if (error) {
    console.error('[CLONE BOT ERROR]', {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint
    });
    throw new Error(error.message || 'Clone failed');
  }

  const newId = (data as string) || null;
  if (!newId) {
    throw new Error('Clone RPC returned no id');
  }

  return newId;
}

export interface DeleteBotResult {
  success: boolean;
  error?: string;
  action_required?: 'close_trades_first';
  bot_status?: string;
  is_running?: boolean;
  is_active?: boolean;
  open_positions?: number;
  pending_orders?: number;
  message?: string;
  bot_id?: string;
  bot_name?: string;
  backtests_deleted?: number;
  deleted_at?: string;
}

export async function deleteBotWithTrades(
  botId: string,
  userId: string,
  forceClose: boolean = false
): Promise<DeleteBotResult> {
  if (!validateUUIDs({ 'Bot ID': botId, 'User ID': userId })) {
    return { success: false, error: 'Invalid UUID format' };
  }

  console.log('[DELETE BOT] Starting deletion:', { botId, userId, forceClose });

  const { data, error } = await supabase.rpc('f_delete_bot_with_trades', {
    p_bot_id: botId,
    p_user_id: userId,
    p_force_close: forceClose
  });

  if (error) {
    console.error('[DELETE BOT] RPC error:', error);
    return { success: false, error: error.message };
  }

  console.log('[DELETE BOT] Result:', data);
  return data as DeleteBotResult;
}

export async function softDeleteBot(botId: string, userId: string): Promise<boolean> {
  if (!validateUUIDs({ 'Bot ID': botId, 'User ID': userId })) {
    return false;
  }

  const { error } = await supabase
    .from('bots')
    .update({
      deleted_at: new Date().toISOString(),
      is_active: false,
      is_running: false
    })
    .eq('id', botId)
    .eq('user_id', userId);

  if (error) {
    console.error('[SOFT DELETE BOT] Error:', error);
    return false;
  }

  return true;
}
